import { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import clsx from 'clsx';
import {
  BanknotesIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';

interface PendingPayout {
  payoutId: string;
  affiliateId: string;
  organizationId: string;
  amount: number;
  paymentMethod: string;
  status: string;
  requestedAt: string;
  affiliateName: string;
  affiliateEmail: string;
  organizationName: string;
}

interface PayoutSummary {
  pendingApproval: {
    count: number;
    totalAmount: number;
  };
  approvedToday: {
    count: number;
    totalAmount: number;
  };
  processedToday: {
    count: number;
    totalAmount: number;
  };
  failedToday: {
    count: number;
    totalAmount: number;
  };
  platformBalance: {
    available: number;
    pending: number;
  } | null;
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

const paymentMethodLabels: Record<string, string> = {
  stripe_connect: 'Stripe Connect',
  stripe_bank: 'Bank Transfer',
  stripe: 'Stripe',
};

export default function AdminPayoutApprovals() {
  const [payouts, setPayouts] = useState<PendingPayout[]>([]);
  const [summary, setSummary] = useState<PayoutSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPayouts, setSelectedPayouts] = useState<Set<string>>(new Set());
  const [isApproving, setIsApproving] = useState(false);
  const [rejectModal, setRejectModal] = useState<{ payoutId: string; affiliateName: string } | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [payoutsRes, summaryRes] = await Promise.all([
        adminApi.getPendingApprovalPayouts(),
        adminApi.getPayoutSummary(),
      ]);

      if (payoutsRes.data.success) {
        setPayouts(payoutsRes.data.data.payouts);
      }
      if (summaryRes.data.success) {
        setSummary(summaryRes.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch payout data:', error);
      toast.error('Failed to load payout data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedPayouts(new Set(payouts.map((p) => p.payoutId)));
    } else {
      setSelectedPayouts(new Set());
    }
  };

  const handleSelectPayout = (payoutId: string) => {
    const newSelected = new Set(selectedPayouts);
    if (newSelected.has(payoutId)) {
      newSelected.delete(payoutId);
    } else {
      newSelected.add(payoutId);
    }
    setSelectedPayouts(newSelected);
  };

  const handleApproveSelected = async () => {
    if (selectedPayouts.size === 0) {
      toast.error('Please select payouts to approve');
      return;
    }

    setIsApproving(true);
    try {
      const response = await adminApi.approvePayouts(Array.from(selectedPayouts));
      if (response.data.success) {
        toast.success(`Successfully approved ${selectedPayouts.size} payout(s)`);
        setSelectedPayouts(new Set());
        fetchData();
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to approve payouts');
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectModal || !rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setIsRejecting(true);
    try {
      const response = await adminApi.rejectPayout(rejectModal.payoutId, rejectReason);
      if (response.data.success) {
        toast.success('Payout rejected successfully');
        setRejectModal(null);
        setRejectReason('');
        fetchData();
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to reject payout');
    } finally {
      setIsRejecting(false);
    }
  };

  const selectedTotal = payouts
    .filter((p) => selectedPayouts.has(p.payoutId))
    .reduce((sum, p) => sum + p.amount, 0);

  const availableBalance = summary?.platformBalance?.available ?? 0;
  const hasSufficientBalance = availableBalance >= selectedTotal;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sage"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-display text-2xl font-bold text-charcoal">Payout Approvals</h1>
        <p className="text-charcoal/60">Review and approve affiliate withdrawal requests</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl bg-warm-white shadow-sm p-5 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-tan" />
          <div className="pl-2">
            <p className="text-sm text-charcoal/60 mb-1">Pending Review</p>
            <p className="font-serif text-2xl text-charcoal leading-none mb-1">
              {summary?.pendingApproval?.count || 0}
            </p>
            <p className="text-xs text-charcoal/50">requests awaiting approval</p>
          </div>
        </div>

        <div className="rounded-xl bg-warm-white shadow-sm p-5 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-rust" />
          <div className="pl-2">
            <p className="text-sm text-charcoal/60 mb-1">Pending Amount</p>
            <p className="font-serif text-2xl text-charcoal leading-none mb-1">
              {formatCurrency(summary?.pendingApproval?.totalAmount || 0)}
            </p>
            <p className="text-xs text-charcoal/50">total pending payouts</p>
          </div>
        </div>

        <div className="rounded-xl bg-warm-white shadow-sm p-5 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-sage" />
          <div className="pl-2">
            <p className="text-sm text-charcoal/60 mb-1">Platform Balance</p>
            <p className="font-serif text-2xl text-charcoal leading-none mb-1">
              {formatCurrency(summary?.platformBalance?.available ?? 0)}
            </p>
            <p className="text-xs text-charcoal/50">available in Stripe</p>
          </div>
        </div>

        <div className="rounded-xl bg-warm-white shadow-sm p-5 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-1 h-full bg-charcoal" />
          <div className="pl-2">
            <p className="text-sm text-charcoal/60 mb-1">Today's Activity</p>
            <p className="font-serif text-2xl text-charcoal leading-none mb-1">
              {(summary?.approvedToday?.count || 0) + (summary?.processedToday?.count || 0)}
            </p>
            <p className="text-xs text-charcoal/50">
              {summary?.approvedToday?.count || 0} approved, {summary?.processedToday?.count || 0} processed
            </p>
          </div>
        </div>
      </div>

      {/* Payment Timing Notice */}
      <div className="rounded-xl bg-tan/10 border border-tan/30 p-4">
        <div className="flex items-start gap-3">
          <InformationCircleIcon className="w-5 h-5 text-tan-dark flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-charcoal">Payment Processing Times</h4>
            <ul className="mt-1 text-sm text-charcoal/70 space-y-1">
              <li>
                <strong>Stripe Connect:</strong> 1-3 business days for funds to reach affiliate's account
              </li>
              <li>
                <strong>Bank Transfer:</strong> 7-10 business days for international transfers
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      {payouts.length > 0 && (
        <div className="flex items-center justify-between bg-warm-white rounded-xl shadow-sm p-4">
          <div className="flex items-center gap-4">
            <span className="text-sm text-charcoal/70">
              {selectedPayouts.size} of {payouts.length} selected
            </span>
            {selectedPayouts.size > 0 && (
              <span className="text-sm font-medium text-charcoal">
                Total: {formatCurrency(selectedTotal)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {selectedPayouts.size > 0 && !hasSufficientBalance && (
              <div className="flex items-center gap-2 text-rust text-sm">
                <ExclamationTriangleIcon className="w-4 h-4" />
                <span>Insufficient platform balance</span>
              </div>
            )}
            <button
              onClick={handleApproveSelected}
              disabled={selectedPayouts.size === 0 || isApproving || !hasSufficientBalance}
              className={clsx(
                'btn btn-primary',
                (selectedPayouts.size === 0 || !hasSufficientBalance) && 'opacity-50 cursor-not-allowed'
              )}
            >
              <CheckCircleIcon className="w-5 h-5 mr-2" />
              {isApproving ? 'Approving...' : `Approve Selected (${selectedPayouts.size})`}
            </button>
          </div>
        </div>
      )}

      {/* Payouts Table */}
      <div className="rounded-xl bg-warm-white shadow-sm border border-cream overflow-hidden">
        {payouts.length === 0 ? (
          <div className="p-12 text-center">
            <BanknotesIcon className="w-12 h-12 text-charcoal/30 mx-auto mb-4" />
            <p className="text-charcoal/60 text-lg">No pending payout requests</p>
            <p className="text-charcoal/40 text-sm mt-1">
              Withdrawal requests from affiliates will appear here for review
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream/50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedPayouts.size === payouts.length}
                      onChange={handleSelectAll}
                      className="rounded border-charcoal/30 text-sage focus:ring-sage"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">
                    Affiliate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">
                    Organization
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">
                    Method
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">
                    Requested
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cream">
                {payouts.map((payout) => (
                  <tr
                    key={payout.payoutId}
                    className={clsx(
                      'hover:bg-cream/30 transition-colors',
                      selectedPayouts.has(payout.payoutId) && 'bg-sage/5'
                    )}
                  >
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedPayouts.has(payout.payoutId)}
                        onChange={() => handleSelectPayout(payout.payoutId)}
                        className="rounded border-charcoal/30 text-sage focus:ring-sage"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-charcoal">{payout.affiliateName}</p>
                        <p className="text-xs text-charcoal/60">{payout.affiliateEmail}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-charcoal">{payout.organizationName}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-serif text-lg text-charcoal">{formatCurrency(payout.amount)}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1.5">
                        {payout.paymentMethod === 'stripe_connect' ? (
                          <CurrencyDollarIcon className="w-4 h-4 text-sage" />
                        ) : (
                          <BanknotesIcon className="w-4 h-4 text-tan-dark" />
                        )}
                        <span className="text-charcoal/70">
                          {paymentMethodLabels[payout.paymentMethod] || payout.paymentMethod}
                        </span>
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 text-charcoal/60">
                        <ClockIcon className="w-4 h-4" />
                        <span>{format(new Date(payout.requestedAt), 'MMM d, yyyy h:mm a')}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() =>
                          setRejectModal({ payoutId: payout.payoutId, affiliateName: payout.affiliateName })
                        }
                        className="text-rust hover:text-rust/80 text-sm font-medium"
                      >
                        Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {rejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/60 backdrop-blur-sm">
          <div className="rounded-xl bg-warm-white shadow-xl w-full max-w-md animate-fade-in border border-cream">
            <div className="px-6 py-4 border-b border-cream flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold text-charcoal">Reject Payout</h3>
              <button
                onClick={() => {
                  setRejectModal(null);
                  setRejectReason('');
                }}
                className="text-charcoal/60 hover:text-charcoal text-2xl leading-none"
              >
                &times;
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3 p-4 bg-rust/10 rounded-lg">
                <XCircleIcon className="w-5 h-5 text-rust flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-charcoal">
                    You are about to reject the payout request for{' '}
                    <strong>{rejectModal.affiliateName}</strong>. The affiliate will be notified and their
                    commissions will be released for future withdrawal.
                  </p>
                </div>
              </div>
              <div>
                <label className="label">Reason for Rejection *</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="input min-h-[100px]"
                  placeholder="Please provide a reason for rejecting this payout..."
                  required
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setRejectModal(null);
                    setRejectReason('');
                  }}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={isRejecting || !rejectReason.trim()}
                  className="btn bg-rust text-white hover:bg-rust/90 flex-1"
                >
                  {isRejecting ? 'Rejecting...' : 'Reject Payout'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
